import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";
import { todayLeftScrollPosition } from "../lib/playerDayCalendar";

const CalendarState = createContext(null);

// UI memory only: survives main-route changes, never a reload or an account change.
export function PlayerCalendarStateProvider({ children }) {
  const { user } = useAuth();
  const memory = useMemo(() => ({ playerId: user?.id, current: null }), [user?.id]);
  return <CalendarState.Provider value={memory}>{children}</CalendarState.Provider>;
}

export function usePlayerCalendarMemory(playerId, readOnly) {
  const shared = useContext(CalendarState);
  const isolated = useMemo(() => ({ playerId, readOnly, current: null }), [playerId, readOnly]);
  return !readOnly && shared?.playerId === playerId ? shared : isolated;
}

export function usePlayerCalendarScroll(memory, selectedDate, rangeStart, todayKey) {
  const nodeRef = useRef(null);
  const view = useRef(null);
  view.current = { selectedDate, rangeStart, todayKey };
  const capture = useCallback((node) => {
    memory.current = { ...view.current, scrollLeft: node.scrollLeft };
  }, [memory]);
  const attach = useCallback((node) => {
    if (nodeRef.current) capture(nodeRef.current);
    nodeRef.current = node;
    if (!node) return;
    const saved = memory.current;
    const current = view.current;
    if (saved?.rangeStart === current.rangeStart && saved.selectedDate === current.selectedDate) {
      node.scrollLeft = saved.scrollLeft;
    } else {
      const day = node.querySelector(`[data-date="${current.selectedDate}"]`)
        || node.querySelector(`[data-date="${current.todayKey}"]`);
      if (day) node.scrollLeft = todayLeftScrollPosition(day.offsetLeft, node.offsetLeft);
    }
    capture(node);
  }, [capture, memory]);
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    // A server-zone/day-boundary change may replace the range. Selection alone
    // never scrolls: in particular, tapping Today has no special alignment rule.
    if (memory.current?.rangeStart !== rangeStart) {
      const day = node.querySelector(`[data-date="${selectedDate}"]`)
        || node.querySelector(`[data-date="${todayKey}"]`);
      if (day) node.scrollLeft = todayLeftScrollPosition(day.offsetLeft, node.offsetLeft);
    }
    capture(node);
  }, [capture, memory, rangeStart, selectedDate, todayKey]);
  return { ref: attach, onScroll: (event) => capture(event.currentTarget) };
}
